import React, { useId, useState } from 'react';
import { authFetch } from '../../utils/auth';
import { generateEntityId } from '../../utils/entityId';
import { CHILD_RELATIONS, ADULT_RELATIONS } from '../../config/relationConstants';
import Button from '../common/Button';

const RELATION_CHOICES = [
  { value: 'sønn', label: 'Sønn' },
  { value: 'datter', label: 'Datter' },
  { value: 'ektefelle', label: 'Ektefelle' },
  { value: 'partner', label: 'Partner' },
  { value: 'mor', label: 'Mor' },
  { value: 'far', label: 'Far' },
  { value: 'søster', label: 'Søster' },
  { value: 'bror', label: 'Bror' },
  { value: 'annet familiemedlem', label: 'Annet familiemedlem' },
];
// "Legg til barn": the relations that mean the parent's own child (both in
// CHILD_RELATIONS, so written as a child). Søster/bror are child relations
// too, but from the parent's side they read as the parent's own siblings.
const OWN_CHILD_RELATIONS = ['sønn', 'datter'];
const OWN_CHILD_CHOICES = RELATION_CHOICES.filter(({ value }) => OWN_CHILD_RELATIONS.includes(value));

// The same person if the names give the same entity id (case, whitespace,
// ø/o, hyphen/space), as the memory store files them. Names with no Latin
// letter or digit give no id; those compare trimmed and case-insensitive.
const sameName = (a, b) => {
  const id = generateEntityId(a);
  return id ? id === generateEntityId(b) : a.trim().toLowerCase() === b.trim().toLowerCase();
};

// The server drops a memory whose content is under 10 characters after
// trimming (validateMemoryQuality in coord-backend/src/lib/memoryQualityControl.js).
// The name row is "{navn} er {relasjon}", so the shortest name it keeps
// depends on the relation: 3 characters with "mor"/"far", else 2 (the floor).
const minNameLength = (relationship) =>
  Math.max(2, 10 - ` er ${relationship || 'familiemedlem'}`.length);

// Did the server store the name row? A created row comes back with the
// content as sent. An updated row comes back as the stored document, which
// can't be traced to one of the rows sent; the server updates only a row of
// the same person that says nearly the same thing, so it counts.
const nameRowStored = (results, nameRow) => Array.isArray(results) && results.some(({ action, memory } = {}) =>
  memory?.entity_id === nameRow.entityId && memory?.subcategory === nameRow.subcategory && (
    (action === 'created' && memory.memory_content === nameRow.content) || action === 'updated'
  ));

/**
 * AddFamilyMemberModal
 *
 * Shared "Legg til familiemedlem" modal + form + submit flow, lifted out of
 * FamilyOverview so the Account → Family page, the dashboard FamilyCard and
 * the søknad overview ("Legg til barn") use the SAME modal (no duplication).
 * On a successful add it calls onAdded(entityId) so the caller can refresh its
 * useFamilyMembers data. It is a success only if the server stored the name
 * row ("{navn} er {relasjon}"); otherwise the modal says so and stays open.
 *
 * Props:
 * - isOpen: whether the modal is rendered
 * - onClose: called when the user cancels or closes
 * - onAdded: called after a member is successfully created, with the entityId
 *   that was written — useFamilyMembers' refresh uses it to retry until the
 *   new row is searchable (Azure AI Search indexing lag) — and the name
 * - childrenOnly: "Legg til barn" (the søknad overview). Only Sønn and Datter
 *   are offered, and one must be chosen, so the member is written as a child
 * - existingNames: names already listed where the modal was opened; a match
 *   (same entity id, see sameName) is not saved and says so
 */
export default function AddFamilyMemberModal({
  isOpen, onClose, onAdded, childrenOnly = false, existingNames = [],
}) {
  const titleId = useId();
  const [newMember, setNewMember] = useState({
    name: '',
    relationship: '',
    birthYear: '',
    notes: ''
  });
  // 'idle' | 'submitting' | 'success' — drives button label + disabled state.
  const [submitState, setSubmitState] = useState('idle');
  const [errorMsg, setErrorMsg] = useState(null);

  const isSubmitting = submitState === 'submitting';
  const isSuccess = submitState === 'success';

  const resetForm = () => {
    setNewMember({ name: '', relationship: '', birthYear: '', notes: '' });
    setSubmitState('idle');
    setErrorMsg(null);
  };

  const handleClose = () => {
    if (isSubmitting) return; // don't allow closing mid-request
    resetForm();
    onClose?.();
  };

  const addFamilyMember = async () => {
    if (isSubmitting || isSuccess) return; // guard against double submit
    if (!newMember.name.trim()) {
      setErrorMsg('Vennligst skriv inn navn');
      return;
    }
    const minLength = minNameLength(newMember.relationship);
    if (newMember.name.trim().length < minLength) {
      setErrorMsg(`Navnet må ha minst ${minLength} tegn.`);
      return;
    }
    const listedName = existingNames.find((name) => sameName(name, newMember.name));
    if (listedName) {
      setErrorMsg(`${listedName.trim()} står allerede på listen.`);
      return;
    }
    if (childrenOnly && !OWN_CHILD_RELATIONS.includes(newMember.relationship)) {
      setErrorMsg('Vennligst velg relasjon');
      return;
    }

    setErrorMsg(null);
    setSubmitState('submitting');

    try {
      const memories = [];

      // Computed once so all memories in this batch — and the onAdded
      // visibility check — agree on the exact same id.
      const entityId = generateEntityId(newMember.name);

      // A manually added family member is always a real person, so it must carry
      // a PERSON entity_type that satisfies the write-time family_members gate
      // (the old 'family_member' value is not a recognized person type). Map the
      // chosen relationship to child/adult DETERMINISTICALLY at write time. This
      // entity_type is the launch-time signal the søknad Step-0 picker uses to
      // keep adults out of the subject list (IntroStep filters entity_type ===
      // 'adult'), so the mapping must be explicit and correct here — there is no
      // structured birthYear to fall back on. Child relations → 'child'; the
      // known adult relations (incl. Ektefelle — the spouse "Ruth" case) → 'adult';
      // anything unspecified/other → 'adult' (the safe person default, so an
      // un-categorized manual add is treated as an adult and excluded from the
      // child-only picker rather than leaking in).
      // Relation sets are single-sourced in config/relationConstants.js so this
      // entity_type mapping can never drift from the confirm step's adult gate.
      let personEntityType;
      if (CHILD_RELATIONS.includes(newMember.relationship)) {
        personEntityType = 'child';
      } else if (ADULT_RELATIONS.includes(newMember.relationship)) {
        personEntityType = 'adult';
      } else {
        // 'annet familiemedlem', empty, or any unrecognized value → safe default.
        personEntityType = 'adult';
      }

      // Basic info memory
      const basicInfo = {
        content: `${newMember.name} er ${newMember.relationship || 'familiemedlem'}`,
        category: 'family_members',
        subcategory: 'basic_info',
        sensitivityLevel: 1,
        entityName: newMember.name,
        entityId,
        entityType: personEntityType,
        source: {
          type: 'manual_entry',
          created_at: new Date().toISOString()
        },
        metadata: {
          extraction_method: 'manual_entry',
          relationship: newMember.relationship
        }
      };
      memories.push(basicInfo);

      // Birth year if provided
      if (newMember.birthYear) {
        const birthInfo = {
          content: `${newMember.name} er født i ${newMember.birthYear}`,
          category: 'family_members',
          subcategory: 'basic_info',
          sensitivityLevel: 1,
          entityName: newMember.name,
          entityId,
          entityType: personEntityType,
          source: {
            type: 'manual_entry',
            created_at: new Date().toISOString()
          },
          metadata: {
            extraction_method: 'manual_entry',
            birth_year: parseInt(newMember.birthYear)
          }
        };
        memories.push(birthInfo);
      }

      // Notes if provided
      if (newMember.notes.trim()) {
        const notesInfo = {
          content: `Om ${newMember.name}: ${newMember.notes}`,
          category: 'family_members',
          subcategory: 'notes',
          sensitivityLevel: 2,
          entityName: newMember.name,
          entityId,
          entityType: personEntityType,
          source: {
            type: 'manual_entry',
            created_at: new Date().toISOString()
          },
          metadata: {
            extraction_method: 'manual_entry',
            notes: newMember.notes
          }
        };
        memories.push(notesInfo);
      }

      // Create memories
      const response = await authFetch('/memories/from-wizard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memories })
      });

      if (response.ok) {
        // The server answers 200 also when it drops rows (validation, or a
        // duplicate of a row already stored). Without the name row the member
        // is not saved; birth year and notes are optional.
        const data = await response.json().catch(() => ({}));
        if (!nameRowStored(data.results, basicInfo)) {
          setSubmitState('idle');
          setErrorMsg(
            `Vi klarte ikke å lagre ${newMember.name.trim()}. Prøv igjen, eller kontakt oss hvis det skjer igjen.`
          );
          return;
        }
        const optionalNotStored = memories.length - data.results.length;
        if (optionalNotStored > 0) {
          // Counts only: the rows are personal data.
          console.warn(
            `[family-add] ${optionalNotStored} of ${memories.length - 1} optional rows not stored ` +
            `(validation dropped ${Number(data.dropped) || 0})`
          );
        }

        // Kick off the caller's refresh — it keeps the current list rendered
        // (no skeleton swap, so this modal stays mounted) and uses the entityId
        // to retry until the new row is searchable. Meanwhile show a brief
        // success confirmation, then close.
        onAdded?.(entityId, newMember.name.trim());
        setSubmitState('success');
        setTimeout(() => {
          resetForm();
          onClose?.();
        }, 900);
      } else {
        setSubmitState('idle');
        setErrorMsg('Kunne ikke legge til familiemedlem. Vennligst prøv igjen.');
      }
    } catch (error) {
      console.error('Error adding family member:', error);
      setSubmitState('idle');
      setErrorMsg('Det oppstod en feil ved tillegging av familiemedlem.');
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg p-6 w-full max-w-md max-h-full overflow-y-auto"
      >
        <h3 id={titleId} className="text-lg font-semibold mb-4">
          {childrenOnly ? 'Legg til barn' : 'Legg til familiemedlem'}
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Navn *
            </label>
            <input
              type="text"
              value={newMember.name}
              onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
              className="w-full p-3 border rounded-lg"
              placeholder="F.eks. Erik, Emma, Thomas"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Relasjon
            </label>
            <select
              value={newMember.relationship}
              onChange={(e) => setNewMember(prev => ({ ...prev, relationship: e.target.value }))}
              className="w-full p-3 border rounded-lg"
            >
              <option value="">Velg relasjon</option>
              {(childrenOnly ? OWN_CHILD_CHOICES : RELATION_CHOICES).map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Fødselsår
            </label>
            <input
              type="number"
              value={newMember.birthYear}
              onChange={(e) => setNewMember(prev => ({ ...prev, birthYear: e.target.value }))}
              className="w-full p-3 border rounded-lg"
              placeholder="F.eks. 2015"
              min="1900"
              max={new Date().getFullYear()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Notater
            </label>
            <textarea
              value={newMember.notes}
              onChange={(e) => setNewMember(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full p-3 border rounded-lg h-20"
              placeholder="Ekstra informasjon..."
            />
          </div>
        </div>

        {/* Inline status — error keeps the modal open; success briefly confirms */}
        {errorMsg && (
          <p role="alert" className="mt-4 text-sm text-error-700 bg-error-50 border border-error-200 rounded-lg p-3">
            {errorMsg}
          </p>
        )}
        {isSuccess && (
          <p className="mt-4 text-sm text-sage-700 bg-sage-50 border border-sage-200 rounded-lg p-3">
            {childrenOnly ? 'Barnet er lagt til!' : 'Familiemedlem lagt til!'}
          </p>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            className="min-h-[48px]"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            className="min-h-[48px]"
            onClick={addFamilyMember}
            disabled={isSuccess}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Legger til…' : isSuccess ? 'Lagt til ✓' : 'Legg til'}
          </Button>
        </div>
      </div>
    </div>
  );
}
